'use client';

import { useRef, useState, useEffect } from 'react';
import { Button } from '@/components/button';
import { PlusIcon, SparklesIcon } from 'lucide-react';
import { MessageList } from '@/components/message-list';
import { InputArea } from '@/components/input-area';
import useMessageStore from '@/store/useMessageStore';
import { useAskQuestion } from '@/hooks/useAskQuestion';
import { useInactivityTimer } from '@/hooks/useInactivityTimer';
import { Message } from './types/mesage';

export function Main() {
  const { messages, addMessage, clearMessages } = useMessageStore();
  const [input, setInput] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [isFirstMessage, setIsFirstMessage] = useState(true);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [isSpeaking, setIsSpeaking] = useState<string | null>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  const {
    askQuestion,
    answer,
    isLoading: isThinking,
    isError,
  } = useAskQuestion();

  // Auto-clear chat after 120 seconds of inactivity
  const { resetTimer } = useInactivityTimer({
    onInactive: () => {
      clearMessages();
      setIsFirstMessage(true);
    },
    inactivityDelay: 120000, // 120 seconds (2 minutes)
    enabled: messages.length > 0, // Only run when there are messages
  });

  const toggleListening = () => {
    setIsListening(!isListening);
  };

  const handleSend = async (question: string) => {
    if (question.trim()) {
      addMessage({ role: 'user', content: question });
      setInput('');
      await askQuestion(question);
      if (isFirstMessage) {
        setIsFirstMessage(false);
      }
    }
  };

  useEffect(() => {
    if (answer) {
      addMessage({ role: 'assistant', content: answer });
    }
  }, [answer, addMessage]);

  const speakMessage = (message: Message) => {
    window.speechSynthesis.cancel();

    const speech = new SpeechSynthesisUtterance(message.content);

    speech.onstart = () => setIsSpeaking(message.id ?? '1');
    speech.onend = () => setIsSpeaking(null);
    speech.onerror = () => setIsSpeaking(null);

    window.speechSynthesis.speak(speech);
  };

  const stopSpeaking = () => {
    window.speechSynthesis.cancel();
    setIsSpeaking(null);
  };

  const handleNewChat = () => {
    clearMessages();
    setIsFirstMessage(true);
  };

  // Track user activity and reset inactivity timer
  useEffect(() => {
    if (messages.length === 0) return; // Don't track if no messages

    const handleUserActivity = () => {
      resetTimer();
    };

    const chatContainer = chatContainerRef.current;
    if (!chatContainer) return;

    // Add event listeners for various user interactions
    chatContainer.addEventListener('mousemove', handleUserActivity);
    chatContainer.addEventListener('click', handleUserActivity);
    chatContainer.addEventListener('keydown', handleUserActivity);
    chatContainer.addEventListener('scroll', handleUserActivity, true); // Use capture phase for scroll
    chatContainer.addEventListener('wheel', handleUserActivity); // Track mouse wheel scrolling
    chatContainer.addEventListener('touchstart', handleUserActivity); // Track touch interactions
    chatContainer.addEventListener('touchmove', handleUserActivity);

    return () => {
      // Cleanup event listeners
      chatContainer.removeEventListener('mousemove', handleUserActivity);
      chatContainer.removeEventListener('click', handleUserActivity);
      chatContainer.removeEventListener('keydown', handleUserActivity);
      chatContainer.removeEventListener('scroll', handleUserActivity, true);
      chatContainer.removeEventListener('wheel', handleUserActivity);
      chatContainer.removeEventListener('touchstart', handleUserActivity);
      chatContainer.removeEventListener('touchmove', handleUserActivity);
    };
  }, [messages.length, resetTimer]);

  const suggestedQuestions = [
    { emoji: '🦾', question: 'Tell me about the engine modifications' },
    { emoji: '🛋️', question: 'What are the interior modifications?' },
    {
      emoji: '🔧',
      question: 'Why is the 2jz is better than the Gallardo v10?',
    },
    { emoji: '🚗', question: 'Describe the car to me' },
  ];
  const handleSuggestionClick = (question: string) => {
    handleSend(question);
  };
  return (
    <div
      ref={chatContainerRef}
      className="flex flex-col h-[100dvh] max-h-[100dvh] bg-[#1E1E1E] text-white p-6 font-roboto"
    >
      <div className="flex justify-between items-center mb-6">
        <Button
          onClick={handleNewChat}
          className="bg-[#2C2C2C] hover:bg-[#3A3A3A] text-white"
        >
          <PlusIcon className="mr-2 h-4 w-4" /> New Chat
        </Button>
      </div>
      {messages.length > 0 ? (
        <>
          <div className="flex-grow overflow-auto">
            <MessageList
              messages={messages}
              scrollAreaRef={scrollAreaRef}
              speakMessage={speakMessage}
              stopSpeaking={stopSpeaking}
              isSpeaking={isSpeaking}
              isThinking={isThinking}
            />
          </div>
          <div className="max-w-4xl mx-auto w-full mt-4">
            <InputArea
              input={input}
              setInput={setInput}
              handleSend={() => handleSend(input)}
              isListening={isListening}
              setIsListening={setIsListening}
              ref={textareaRef}
            />
          </div>
        </>
      ) : (
        <div className="flex-grow flex flex-col justify-center items-center overflow-auto">
          <h1 className="text-5xl font-bold mb-12 text-center">
            Ask me
            <span className="text-[#e66b3c]"> anything </span>
            about the car!
          </h1>
          <div className="w-full max-w-4xl">
            <InputArea
              input={input}
              setInput={setInput}
              handleSend={() => handleSend(input)}
              isListening={isListening}
              setIsListening={setIsListening}
              ref={textareaRef}
            />
          </div>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-4xl">
            {suggestedQuestions.map((item, index) => (
              <Button
                key={index}
                onClick={() => handleSuggestionClick(item.question)}
                className="bg-[#2C2C2C] hover:bg-[#3A3A3A] h-15 sm:h-auto text-white text-left justify-start overflow-hidden"
              >
                <span className="mr-2 text-2xl">{item.emoji}</span>
                <span className="whitespace-normal break-words text-lg">
                  {item.question}
                </span>
              </Button>
            ))}
          </div>
        </div>
      )}
      {isError && (
        <p className="text-red-500 mt-4">
          Due to the high number of requests, server limits have been met,
          please try again later.
        </p>
      )}
    </div>
  );
}
